import { type FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useRefinementList } from 'react-instantsearch';
import { SearchIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { EntityType } from '@/types/entity';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export interface RefinementItem {
  label: string;
  value: string;
  count: number;
  isRefined: boolean;
}

export interface HierarchicalItem extends RefinementItem {
  children?: Array<HierarchicalItem>;
}

interface RefinementListProps {
  attribute: string;
  // Used to build checkbox ids and the search placeholder.
  label: string;
  searchable?: boolean;
  searchLabel?: string;
  options?: Record<string, string>;
  fieldType?: string;
  // Fired once when a refinement for this attribute is first detected, so a parent
  // header can auto-expand. Should be stable (wrap in useCallback).
  onRefinedItemsDetected?: () => void;
}

export const filterLabelClasses = 'text-[13px] font-normal line-clamp-1 break-all text-left';

export const formatDateFacetLabel = (label: string, fieldType: 'date' | 'datetime'): string => {
  if (!/^\d+$/.test(label)) {
    return label;
  }
  const fmt = fieldType === 'datetime' ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd';
  return format(new Date(Number(label) * 1000), fmt);
};

// Sort function for refinement items
export const sortRefinementItems = (items: Array<RefinementItem>): Array<RefinementItem> => {
  return [...items].sort((a, b) => {
    // 1. Refined items come first
    if (a.isRefined && !b.isRefined) {
      return -1;
    }
    if (!a.isRefined && b.isRefined) {
      return 1;
    }

    // 2. Sort by count (highest first)
    if (a.count !== b.count) {
      return b.count - a.count;
    }

    // 3. Sort alphabetically by label
    return a.label.localeCompare(b.label);
  });
};

export const parseHierarchicalItems = (items: Array<RefinementItem>): Array<HierarchicalItem> => {
  const root: Array<HierarchicalItem> = [];

  items.forEach((item) => {
    const parts = item.value.split('||');
    let currentLevel = root;

    parts.forEach((part, index) => {
      let existingItem = currentLevel.find((i) => i.label === part);

      if (!existingItem) {
        existingItem = {
          label: part,
          value: parts.slice(0, index + 1).join('||'),
          count: item.count,
          isRefined: item.isRefined,
          children: [],
        };
        currentLevel.push(existingItem);
      }

      if (index === parts.length - 1) {
        existingItem.count = item.count;
        existingItem.isRefined = item.isRefined;
      }

      currentLevel = existingItem.children!;
    });
  });

  return root;
};

/**
 * Server-searches one attribute's facet values by typed text (Typesense SFFV), instead of
 * a client-side substring match limited to whatever the initial load happened to return.
 */
export const useSearchableRefinementItems = (attribute: string, searchQuery: string) => {
  const { items, refine, canRefine, searchForItems, isFromSearch } = useRefinementList({ attribute, limit: 1000 });

  // Drive the server facet-search from the box text.
  useEffect(() => {
    searchForItems(searchQuery);
  }, [searchQuery, searchForItems]);

  // refine() triggers a main search that resets SFFV to the full list; re-apply the typed
  // filter when the set of refined values changes so the search persists after a selection.
  const refinedKey = items
    .filter((i) => i.isRefined)
    .map((i) => i.value)
    .sort()
    .join('\0');
  const prevRefinedKey = useRef(refinedKey);
  useEffect(() => {
    if (refinedKey !== prevRefinedKey.current) {
      prevRefinedKey.current = refinedKey;
      if (searchQuery) {
        searchForItems(searchQuery);
      }
    }
  }, [refinedKey, searchQuery, searchForItems]);

  const sortedItems = useMemo(() => sortRefinementItems(items), [items]);

  /**
   * Snapshot the last real search result so a mid-refetch flash (isFromSearch=false,
   * transient full list and counts) doesn't repopulate what's shown, until it settles.
   */
  const [searchSnapshot, setSearchSnapshot] = useState<Array<RefinementItem>>([]);
  useEffect(() => {
    if (searchQuery && isFromSearch) {
      setSearchSnapshot(sortedItems);
    }
  }, [searchQuery, isFromSearch, sortedItems]);

  const heldItems = useMemo(() => {
    // No hold when not filtering, when showing fresh results, or before any snapshot exists
    // (e.g. while the first facet-search for the typed text is still in flight).
    if (!searchQuery || isFromSearch || searchSnapshot.length === 0) {
      return sortedItems;
    }
    // Keep the snapshot's values and counts; only the toggled item's isRefined tracks live state.
    return searchSnapshot.map((held) => {
      const current = items.find((i) => i.value === held.value);
      return current ? { ...held, isRefined: current.isRefined } : held;
    });
  }, [searchQuery, isFromSearch, sortedItems, searchSnapshot, items]);

  return { items, heldItems, refine, canRefine };
};

/**
 * Headerless refinement list for a single attribute, with its own search box when
 * `searchable`. Derives all state from useSearchableRefinementItems — no local URL
 * sync; the search routing context is the single writer of search URL state.
 */
export const RefinementList: FC<RefinementListProps> = ({
  attribute,
  label,
  searchable,
  searchLabel,
  options,
  fieldType,
  onRefinedItemsDetected,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const tCommon = useTranslations('common');
  const setPageIndex = useTableStore((s) => s.setPageIndex);

  const { items, heldItems, refine, canRefine } = useSearchableRefinementItems(attribute, searchQuery);

  // Notify parent once when this attribute has an active refinement (auto-expand).
  const refinedItemsDetected = useRef(false);
  useEffect(() => {
    if (items.some((i) => i.isRefined) && !refinedItemsDetected.current) {
      onRefinedItemsDetected?.();
      refinedItemsDetected.current = true;
    }
  }, [items, onRefinedItemsDetected]);

  const visibleItems = useMemo(() => (showAll ? heldItems : heldItems.slice(0, 10)), [heldItems, showAll]);
  const canShowMore = heldItems.length > 10;
  const hierarchicalItems = useMemo(() => parseHierarchicalItems(visibleItems), [visibleItems]);

  const handleCheckboxChange = (value: string) => {
    refine(value);
    setPageIndex(EntityType.Asset, 0);
  };

  const renderItems = (renderTargets: Array<HierarchicalItem>, level = 0) =>
    renderTargets.map((renderItem) => (
      <Fragment key={renderItem.value}>
        <div className="flex items-center space-x-2" style={level > 0 ? { marginLeft: `${level * 24}px` } : {}}>
          <Checkbox
            data-cy={`search-facet-${attribute}-checkbox`}
            className="rounded-[3px] border-icon-base"
            id={`${label}-${renderItem.value}`}
            value={renderItem.isRefined ? 1 : 0}
            checked={renderItem.isRefined}
            onCheckedChange={() => handleCheckboxChange(renderItem.value)}
          />
          <label
            htmlFor={`${label}-${renderItem.value}`}
            className={cn(
              'flex w-full items-center justify-between text-sm font-normal leading-none hover:cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
              renderItem.isRefined ? 'text-black dark:text-white' : 'text-neutral-600 dark:text-neutral-400',
            )}
          >
            <p className={filterLabelClasses}>
              {renderItem.label === 'true'
                ? tCommon('true')
                : renderItem.label === 'false'
                  ? tCommon('false')
                  : fieldType === 'date' || fieldType === 'datetime'
                    ? formatDateFacetLabel(renderItem.label, fieldType)
                    : (options?.[renderItem.label] ?? renderItem.label)}
            </p>
            {renderItem.count && <p className="ml-2 rounded-full font-light">{renderItem.count}</p>}
          </label>
        </div>
        {renderItem.children && renderItems(renderItem.children, level + 1)}
      </Fragment>
    ));

  if (!canRefine) {
    return null;
  }

  return (
    <div className="space-y-3 py-2 pr-2 pl-4">
      {searchable && (
        <Input
          prependIcon={<SearchIcon className="mr-2 icon-6 shrink-0 text-icon-base opacity-50" strokeWidth={1.5} />}
          type="search"
          placeholder={`Search ${searchLabel ? searchLabel.toLowerCase() : ''}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mb-3 h-8 text-sm"
          data-cy={`search-facet-${attribute}-search-input`}
        />
      )}
      {searchQuery && hierarchicalItems?.length === 0 ? (
        <p className={filterLabelClasses}>{tCommon('noResults')}</p>
      ) : (
        renderItems(hierarchicalItems)
      )}
      {canShowMore && (
        <div className="flex justify-center">
          <button
            data-cy={`search-facet-${attribute}-show-more`}
            onClick={() => setShowAll((prev) => !prev)}
            className="m-0 inline-block rounded-md px-2 py-1 text-sm font-medium text-text-base"
          >
            {showAll ? tCommon('showLess') : tCommon('showMore')}
          </button>
        </div>
      )}
    </div>
  );
};
